/**
 * HTTP client configuration following FastAPI best practices.
 * This module provides the base HTTP client setup, while specific API endpoints
 * are handled by dedicated service classes.
 */
import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { auth } from './firebase';

export interface HttpConfig extends AxiosRequestConfig {
  /** Base URL for the API */
  baseURL?: string;
  /** API version prefix */
  apiPrefix?: string;
  /** Whether to include auth token in requests */
  requiresAuth?: boolean;
}

export class HttpClient {
  private instance: AxiosInstance;
  private config: HttpConfig;

  constructor(config: HttpConfig) {
    this.config = {
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
      apiPrefix: '',  // We'll handle API versioning in the service layer
      requiresAuth: true,
      ...config,
    };

    this.instance = axios.create({
      baseURL: this.config.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      ...config,
    });

    if (this.config.requiresAuth) {
      this.setupAuthInterceptor();
    }
    this.setupErrorHandling();
  }

  private setupAuthInterceptor() {
    this.instance.interceptors.request.use(async (config) => {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  private setupErrorHandling() {
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error('API Error:', {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          });
        } else if (error.request) {
          // The request was made but no response was received
          console.error('Network Error:', error.request);
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Request Error:', error.message);
        }
        return Promise.reject(error);
      }
    );
  }

  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.instance.get<T>(url, config);
  }

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.post<T>(url, data, config);
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.put<T>(url, data, config);
  }

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.instance.patch<T>(url, data, config);
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.instance.delete<T>(url, config);
  }
}

// Create default HTTP client instance
export const http = new HttpClient({});
